import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { nextTick } from "vue";
import posthog from "posthog-js";
import DevelopmentPlatform from "../views/DevelopmentPlatform.vue";
import Cores from "../views/Cores.vue";
import DataSources from "../views/DataSources.vue";
import GithubAuth from "../views/GithubAuth.vue";
import Usage from "../views/Usage.vue";
import Home from "../views/Home.vue";

import Repositories from "../views/Repositories.vue";
import Apps from "../views/Apps.vue";

/*
 *Disabled till api migration for pipelines is done
 // import Pipelines from "../views/Pipelines.vue";
 // import PipelineDetail from "../views/PipelineDetail.vue";
 */
import TaskManager from "../views/TaskManager.vue";
import TaskDetail from "../views/TaskDetail.vue";
import RepositoryDetail from "@/components/Repositories/RepositoryDetail.vue";

import Settings_Profile from "@/components/Settings/Profile.vue";
import Settings_General from "@/components/Settings/General.vue";
import Settings_CloudEngineAPI from "@/components/Settings/ComputeEngineApi/CloudEngineAPI.vue";
import Settings_AccountLogs from "@/components/Settings/AccountLogs.vue";
import Settings_Organizations from "@/components/Settings/Organization/Organizations.vue";
import Settings from "@/views/Settings.vue";
import WelcomePage from "@/views/WelcomePage.vue";
import Billing from "@/views/Billing.vue";
import NotFound from "@/views/NotFound.vue";
import BillingPanel from "@/components/BillingPanel.vue";
import Subscription from "@/components/Subscription.vue";

const routes: Array<RouteRecordRaw> = [
	{
		/*
		 *  Redirects to the default route.
		 */
		path: "/",
		redirect: "/home",
		beforeEnter: (to, from, next) => {
			/*
			 *  Check for history-fallback redirect.
			 *  Source: https://stackoverflow.com/a/61571528
			 */
			if (sessionStorage.getItem("redirect") != null) {
				const redirect = sessionStorage.redirect;
				delete sessionStorage.redirect;
				// Only allow root-relative paths to prevent open redirect (blocks //evil.com)
				if (
					typeof redirect === "string" &&
					redirect.startsWith("/") &&
					!redirect.startsWith("//") &&
					!redirect.includes("://")
				) {
					next({ path: redirect });
				} else {
					next("/home");
				}
			} else {
				next("/home");
			}
		},
	},
	{
		path: "/home",
		name: "Home",
		meta: {
			requiresAuth: true,
			title: "Home",
		},
		component: Home,
	},
	{
		path: "/welcome",
		name: "Welcome",
		meta: {
			requiresAuth: true,
		},
		component: WelcomePage,
	},
	{
		path: "/launch",
		name: "CodeEditor",
		meta: {
			requiresAuth: true,
			title: "Code Playground",
		},
		component: DevelopmentPlatform,
	},
	{
		path: "/cores",
		name: "Cores",
		meta: {
			requiresAuth: true,
			title: "Cores",
		},
		component: Cores,
	},
	{
		path: "/datasources",
		name: "DataSources",
		meta: {
			requiresAuth: true,
			title: "Data and Sensors",
		},
		component: DataSources,
	},
	{
		path: "/repositories",
		name: "Repositories",
		meta: {
			requiresAuth: true,
			title: "Repositories",
		},
		// component: () => import(/* webpackChunkName: "repositories" */ "@/views/Repositories.vue"),
		component: Repositories,
	},
	{
		path: "/repositories/detail",
		name: "RepositoryDetail",
		meta: {
			requiresAuth: true,
			title: "Repository Detail",
		},
		props: (route) => ({ repositoryID: route.query.repositoryID as string }),
		// component: () =>
		// 	import(/* webpackChunkName: "repositories" */ "@/components/Repositories/RepositoryDetail.vue"),
		component: RepositoryDetail,
	},
	{
		path: "/benchmarking",
		name: "Benchmarks",
		meta: {
			requiresAuth: true,
			title: "Benchmarks",
		},
		component: () => import(/* webpackChunkName: "benchmarks" */ "@/views/Benchmarks.vue"),
	},
	{
		path: "/benchmarking/:benchmarkID",
		name: "BenchmarkRun",
		meta: {
			requiresAuth: true,
			title: "Benchmark Run",
		},
		props: true,
		component: () => import(/* webpackChunkName: "benchmarks" */ "@/views/BenchmarkRun.vue"),
	},
	{
		path: "/apps",
		name: "Apps",
		meta: {
			requiresAuth: true,
			title: "API App Highlights",
		},
		// component: () => import(/* webpackChunkName: "repositories" */ "@/views/Repositories.vue"),
		component: Apps,
	},
	/*
	  * Disabled till api migration for pipelines is done
	// {
	// 	path: "/pipelines",
	// 	name: "Pipelines",
	// 	meta: {
	// 		requiresAuth: true,
	// 	},
	// 	component: Pipelines,
	// },
	// {
	// 	path: "/pipelines/:pipeline",
	// 	name: "PipelineDetail",
	// 	meta: {
	// 		requiresAuth: true,
	// 	},
	// 	component: PipelineDetail,
	// },
	*/
	{
		path: "/tasks",
		name: "TaskManager",
		meta: {
			requiresAuth: true,
			title: "Builds and Tasks",
		},
		// component: () => import(/* webpackChunkName: "tasks" */ "@/views/TaskManager.vue"),
		component: TaskManager,
	},
	{
		path: "/tasks/:id",
		name: "TaskDetail",
		meta: {
			requiresAuth: true,
			title: "Task Detail",
		},
		props: (route) => ({
			id: route.params.id,
			initialTaskData: route.params.initialTaskData,
		}),
		// component: () => import(/* webpackChunkName: "tasks" */ "@/views/TaskDetail.vue"),
		component: TaskDetail,
		beforeEnter: (from, to, next) => {
			return next();
		},
	},
	{
		path: "/usage",
		name: "Usage",
		meta: {
			requiresAuth: true,
			title: "Usage",
		},
		component: Usage,
	},
	{
		path: "/billing",
		meta: {
			requiresAuth: true,
			title: "Billing",
		},
		component: Billing,
		children: [
			{
				path: "",
				name: "Billing",
				component: BillingPanel,
			},
			{
				path: "subscribe",
				name: "Subscribe",
				meta: {
					requiresAuth: true,
				},
				component: Subscription,
			},
		],
	},
	{
		path: "/subscribe",
		redirect: "/billing",
	},
	{
		path: "/external/oauth/github/auth",
		name: "GithubAuth",
		meta: {
			requiresAuth: true,
		},
		component: GithubAuth,
		props: (route) => ({
			code: route.query.code,
			connect: route.query.connect,
			state: route.query.state,
			error: route.query.error,
			errorDescription: route.query.error_description,
		}),
	},
	{
		path: "/settings",
		meta: {
			requiresAuth: true,
			title: "Settings",
		},
		children: [
			{
				path: "",
				redirect: { name: "Settings_profile" },
			},
			{
				path: "profile",
				name: "Settings_profile",
				// component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/Profile.vue"),
				component: Settings_Profile,
			},
			{
				path: "general",
				name: "Settings_general",
				// component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/General.vue"),
				component: Settings_General,
			},
			{
				path: "account",
				redirect: { name: "Settings_profile" },
			},
			{
				path: "logs",
				name: "Settings_accountLogs",
				component: Settings_AccountLogs,
			},
			{
				path: "api",
				name: "Settings_cloudEngineAPI",
				// component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/ComputeEngineApi/CloudEngineAPI.vue"),
				component: Settings_CloudEngineAPI,
			},
			{
				path: "organizations",
				name: "Settings_organizations",
				component: Settings_Organizations,
			},
			// {
			// 	path: "billing",
			// 	name: "Settings_billing",
			// 	component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/Billing.vue"),
			// },
			// {
			// 	path: "integrations",
			// 	name: "Settings_integrations",
			// 	component: () => import(/* webpackChunkName: "settings" */ "@/components/Settings/Integrations.vue"),
			// },
			{
				path: ":pathMatch(.*)*",
				redirect: { name: "Settings_profile" },
			},
		],

		// component: () => import(/* webpackChunkName: "settings" */ "@/views/Settings.vue"),
		component: Settings,
	},
	{
		/*
		 *  Wildcard route that shows a 404 Not Found page.
		 */
		path: "/:pathMatch(.*)*",
		name: "NotFound",
		component: NotFound,
		meta: {
			title: "Page Not Found",
		},
	},
];

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes,
	scrollBehavior(_to, _from, savedPosition) {
		if (savedPosition) return savedPosition;
		return { top: 0 };
	},
});

/*
 * Setup router hooks
 */

//*  Send page view to analytics on load
router.afterEach((to) => {
	nextTick(() => {
		posthog?.capture("$pageview", {
			$current_url: to.fullPath,
		});
	});
});

export default router;
